import { useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BulkEditPointOfInterestsQuery, BulkEditTemplateQuery } from '@/__generated__/graphql';
import { BulkEditColumnDef, BulkEditFormValues } from './types';
import { GENERAL_FIELD_CONFIGS } from './constants';

type Poi = BulkEditPointOfInterestsQuery['pointOfInterests'][number];
type TemplateField = NonNullable<
  BulkEditTemplateQuery['pointOfInterestTemplate']
>['fields'][number];

export const buildInitialValues = (
  pois: Poi[],
  columns: BulkEditColumnDef[]
): BulkEditFormValues => {
  const values: BulkEditFormValues = {};
  pois.forEach((poi) => {
    const poiValues: Record<string, string> = {};
    columns.forEach((col) => {
      if (col.isGeneral) {
        if (col.key === 'assembly') {
          poiValues[col.key] = poi.assembly?.tagName ?? '';
        } else if (col.key === 'dueDate') {
          const rawValue = (poi as Record<string, unknown>)[col.key];
          if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
            const date = new Date(Number(rawValue));
            poiValues[col.key] = Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
          } else {
            poiValues[col.key] = '';
          }
        } else {
          const rawValue = (poi as Record<string, unknown>)[col.key];
          poiValues[col.key] = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
        }
      } else {
        const dataKey = col.fieldKey ?? col.key;
        const templateValue = (poi.templateFieldData as Record<string, unknown> | null)?.[dataKey];
        poiValues[col.key] =
          templateValue !== null && templateValue !== undefined ? String(templateValue) : '';
      }
    });
    values[poi.id] = poiValues;
  });
  return values;
};

const buildValidationSchema = (pois: Poi[], columns: BulkEditColumnDef[]) => {
  const poiSchema: Record<string, Yup.Schema> = {};
  pois.forEach((poi) => {
    const fieldSchema: Record<string, Yup.Schema> = {};
    columns.forEach((col) => {
      if (col.isBulkEditable) {
        fieldSchema[col.key] = Yup.string();
      }
    });
    poiSchema[poi.id] = Yup.object(fieldSchema);
  });
  return Yup.object(poiSchema);
};

export const buildBulkEditColumns = (
  templateFields: TemplateField[],
  visibleGeneralKeys: string[]
): BulkEditColumnDef[] => {
  const generalCols: BulkEditColumnDef[] = GENERAL_FIELD_CONFIGS.filter((cfg) =>
    visibleGeneralKeys.includes(cfg.key)
  ).map((cfg) => ({
    key: cfg.key,
    label: cfg.label,
    fieldType: cfg.fieldType,
    isBulkEditable: cfg.isBulkEditable,
    options: cfg.options,
    isGeneral: true,
    isMandatory: cfg.isMandatory,
  }));

  const templateCols: BulkEditColumnDef[] = templateFields.map((field) => {
    const optionsFromData = (field.optionsData ?? []).map((opt) => ({
      value: opt?.value ?? '',
      label: opt?.value ?? '',
    }));
    const optionsFromStrings = (field.options ?? []).map((opt) => ({
      value: opt ?? '',
      label: opt ?? '',
    }));
    const fieldKey = field.key ?? '';
    return {
      key: field.id,
      fieldKey,
      label: field.name,
      fieldType: field.type,
      // A field with no key cannot be submitted — treat as non-editable regardless of DB flag
      isBulkEditable: Boolean(field.isBulkEditable) && Boolean(fieldKey),
      options: optionsFromData.length > 0 ? optionsFromData : optionsFromStrings,
      isGeneral: false,
    };
  });

  return [...generalCols, ...templateCols];
};

type Options = {
  pois: Poi[];
  columns: BulkEditColumnDef[];
  onSubmit: (values: BulkEditFormValues) => Promise<void>;
};

export const useBulkEditForm = ({ pois, columns, onSubmit }: Options) => {
  const initialValues = useMemo(() => buildInitialValues(pois, columns), [pois, columns]);
  const validationSchema = useMemo(() => buildValidationSchema(pois, columns), [pois, columns]);

  const formik = useFormik<BulkEditFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: false,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit,
  });

  return formik;
};
